/**
 * Tema para ApexCharts consistente con el CRM, AHORA mode-aware (claro/oscuro).
 * `withDarkTheme(opts)` (nombre histórico) devuelve el base del modo activo mergeado
 * con las opciones del caller. El modo lo setea CrmThemeProvider con setChartMode();
 * como el árbol remonta al togglear (key={mode}), los charts reconstruyen sus options.
 */
import type { ApexOptions } from 'apexcharts';

let currentMode: 'light' | 'dark' = 'dark';
export function setChartMode(mode: 'light' | 'dark') { currentMode = mode; }

export const APEX_DARK_THEME: ApexOptions = {
  theme: { mode: 'dark' },
  chart: {
    foreColor: 'rgba(255,255,255,0.6)',
    background: 'transparent',
    fontFamily: 'Inter, sans-serif',
  },
  tooltip: {
    theme: 'dark',
    fillSeriesColor: false,
    style: { fontSize: '12px', fontFamily: 'Inter, sans-serif' },
  },
  xaxis: {
    labels: { style: { colors: 'rgba(255,255,255,0.55)' } },
    axisBorder: { color: 'rgba(255,255,255,0.10)' },
    axisTicks: { color: 'rgba(255,255,255,0.10)' },
  },
  yaxis: {
    labels: { style: { colors: 'rgba(255,255,255,0.55)' } },
  },
  legend: {
    labels: { colors: 'rgba(255,255,255,0.7)' },
  },
  grid: { borderColor: 'rgba(255,255,255,0.06)' },
  dataLabels: { enabled: false },
  markers: { size: 0, hover: { size: 5 } },
};

export const APEX_LIGHT_THEME: ApexOptions = {
  theme: { mode: 'light' },
  chart: {
    foreColor: 'rgba(0,0,0,0.6)',
    background: 'transparent',
    fontFamily: 'Inter, sans-serif',
  },
  tooltip: {
    theme: 'light',
    fillSeriesColor: false,
    style: { fontSize: '12px', fontFamily: 'Inter, sans-serif' },
  },
  xaxis: {
    labels: { style: { colors: 'rgba(0,0,0,0.6)' } },
    axisBorder: { color: 'rgba(0,0,0,0.12)' },
    axisTicks: { color: 'rgba(0,0,0,0.12)' },
  },
  yaxis: {
    labels: { style: { colors: 'rgba(0,0,0,0.6)' } },
  },
  legend: {
    labels: { colors: 'rgba(0,0,0,0.75)' },
  },
  grid: { borderColor: 'rgba(0,0,0,0.08)' },
  dataLabels: { enabled: false },
  markers: { size: 0, hover: { size: 5 } },
};

/**
 * Deep merge de las opciones default (del modo activo) con las custom del caller.
 * El caller siempre gana sobre los defaults.
 */
export function withDarkTheme(custom: ApexOptions = {}): ApexOptions {
  const base = currentMode === 'light' ? APEX_LIGHT_THEME : APEX_DARK_THEME;
  return deepMerge(base, custom) as ApexOptions;
}

function deepMerge(a: any, b: any): any {
  if (a === undefined || a === null) return b;
  if (b === undefined || b === null) return a;
  if (Array.isArray(a) || Array.isArray(b)) return b;
  if (typeof a !== 'object' || typeof b !== 'object') return b;
  const result: Record<string, any> = { ...a };
  for (const key of Object.keys(b)) {
    result[key] = key in a ? deepMerge(a[key], b[key]) : b[key];
  }
  return result;
}
